import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Sparkles, UserCog, Clock, StickyNote, IdCard, Syringe, CreditCard, Users, Globe, Bot } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { STATUS_LABELS, STATUS_ORDER, STATUS_COLORS, SOURCE_LABELS } from "@/lib/agendaConstants";
import { AGENDA, PATIENTS, PAYMENTS } from "@/constants/testIds";
import TreatmentModal from "@/components/patients/TreatmentModal";
import SquareChargeModal from "@/components/payments/SquareChargeModal";

const SOURCE_ICONS = { manual: Users, public_booking: Globe, whatsapp_agent: Bot };

export default function AppointmentDetailModal({ open, onOpenChange, appointment: appointmentProp, professionals, services, products, onEdit, onChanged }) {
  const [appointment, setAppointment] = useState(appointmentProp);
  const [updating, setUpdating] = useState(false);
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canRegisterTreatment = user?.role === "admin" || user?.role === "professional";
  const canCharge = (user?.role === "admin" || user?.role === "professional") && appointment?.status !== "cancelled";

  useEffect(() => { setAppointment(appointmentProp); }, [appointmentProp]);

  if (!appointment) return null;

  const changeStatus = async (status) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/appointments/${appointment.id}/status`, { status });
      setAppointment(data);
      toast({ title: "Estado actualizado" });
      onChanged();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);
  const timeLabel = `${start.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })} \u00b7 ${start.toTimeString().slice(0, 5)} - ${end.toTimeString().slice(0, 5)}`;
  const colors = STATUS_COLORS[appointment.status];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={AGENDA.detailModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl capitalize" data-testid={AGENDA.detailPatientName}>{appointment.patient_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1 text-sm">
          <div className="flex items-center gap-2 text-foreground" data-testid={AGENDA.detailTime}>
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" /> <span className="capitalize">{timeLabel}</span>
          </div>
          <div className="flex items-center gap-2 text-foreground" data-testid={AGENDA.detailService}>
            <Sparkles className="w-4 h-4 text-muted-foreground shrink-0" /> {appointment.service_name}
          </div>
          <div className="flex items-center gap-2 text-foreground" data-testid={AGENDA.detailProfessional}>
            <UserCog className="w-4 h-4 text-muted-foreground shrink-0" /> {appointment.professional_name}
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-xs" data-testid={AGENDA.detailOrigin}>
            {(() => { const SourceIcon = SOURCE_ICONS[appointment.source] || Users; return <SourceIcon className="w-3.5 h-3.5 shrink-0" />; })()}
            {SOURCE_LABELS[appointment.source] || SOURCE_LABELS.manual}
          </div>
          {appointment.patient_phone && (
            <div className="flex items-center gap-2 text-foreground">
              <User className="w-4 h-4 text-muted-foreground shrink-0" /> {appointment.patient_phone}
            </div>
          )}
          {appointment.notes && (
            <div className="flex items-start gap-2 text-muted-foreground" data-testid={AGENDA.detailNotes}>
              {/* Fix BE (2026-10-31): el comentario respeta los saltos de línea */}
              <StickyNote className="w-4 h-4 shrink-0 mt-0.5" /> <span className="whitespace-pre-line break-words">{appointment.notes}</span>
            </div>
          )}

          <div className="pt-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Estado</label>
            <Select value={appointment.status} onValueChange={changeStatus} disabled={updating}>
              <SelectTrigger
                data-testid={AGENDA.detailStatusSelect}
                style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
                className="border"
              >
                <SelectValue>{STATUS_LABELS[appointment.status]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Fix BF (2026-11-03): con 5 o 6 botones no entraban en una fila y se salian del recuadro ("Cobrar" y "Cerrar"
            quedaban cortados): ahora pasan a la fila de abajo. */}
        <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
          {appointment.status !== "cancelled" && (
            <Button
              variant="outline"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => changeStatus("cancelled")}
              disabled={updating}
              data-testid={AGENDA.detailCancelAppointmentButton}
            >
              Cancelar turno
            </Button>
          )}
          {canCharge && (
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); setChargeModalOpen(true); }}
              data-testid={PAYMENTS.appointmentChargeButton}
            >
              <CreditCard className="w-4 h-4" /> Cobrar
            </Button>
          )}
          {canRegisterTreatment && appointment.status === "completed" && (
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); setTreatmentModalOpen(true); }}
              data-testid={PATIENTS.agendaRegisterTreatmentButton}
            >
              <Syringe className="w-4 h-4" /> Registrar tratamiento
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/pacientes/${appointment.patient_id}`)}
            data-testid={PATIENTS.agendaViewProfileButton}
          >
            <IdCard className="w-4 h-4" /> Ver ficha
          </Button>
          <Button variant="outline" onClick={onEdit} data-testid={AGENDA.detailEditButton}>Editar</Button>
          <Button onClick={() => onOpenChange(false)} data-testid={AGENDA.detailCloseButton}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>

      <TreatmentModal
        open={treatmentModalOpen}
        onOpenChange={setTreatmentModalOpen}
        patientId={appointment.patient_id}
        professionals={professionals || []}
        services={services || []}
        products={products || []}
        currentUser={user}
        defaults={{
          professional_id: appointment.professional_id,
          service_id: appointment.service_id,
          appointment_id: appointment.id,
        }}
        onCreated={(created) => {
          toast({ title: "Tratamiento registrado" });
          if (created?.low_stock_warnings?.length > 0) {
            toast({ title: "Atención: stock bajo", description: created.low_stock_warnings.join(" \u00b7 "), variant: "destructive" });
          }
          setTreatmentModalOpen(false);
        }}
      />

      <SquareChargeModal
        open={chargeModalOpen}
        onOpenChange={(v) => { setChargeModalOpen(v); if (!v) onChanged(); }}
        patient={{ id: appointment.patient_id }}
        appointmentId={appointment.id}
        serviceId={appointment.service_id}
        defaultAmount={appointment.service_price}
        defaultNote={appointment.service_name}
        onCreated={onChanged}
      />
    </Dialog>
  );
}
